import React from "react";
import useOpenDocument from "../../../hooks/useOpenDocument";
import EmailViewPort from "../../../components/emails";
import useGetEmailByGraphId from "../hooks/useGetEmailByGraphId";
import { useFiledMailMessageControls } from "../../../hooks/useMailMessageControls";
import {EmailAttachment, EmailDisplayContents} from "../../../components/emails/types";
import { openConfirmModal } from "../../../components/modals/ConfirmModal";

export type ClientEmailProps = {
    mailId: string
    setMailId: (id: string | null) => void
    masterAccountId: string
    showProjectSelect?: boolean
}

const ClientEmail = ({
    mailId,
    setMailId,
    masterAccountId,
    showProjectSelect = true
} : Readonly<ClientEmailProps>) => {
    const { data, isFetching, error } = useGetEmailByGraphId(mailId);
    const { onUnFile, onForward, onReply, onReplyAll, onReFile } = useFiledMailMessageControls(data, masterAccountId);
    const { openDocument, isLoading: isOpening } = useOpenDocument();

    const handleOnReFile = (email: EmailDisplayContents) =>
        onReFile(email).then((filed) => {
            // Remove the email from view if it has been re-filed to a different master account
            if (masterAccountId !== filed.masterAccountId) {
                setMailId(null);
            }
        });

    const handleOnForward = () => onForward({ masterAccountId, projectId: data?.projectId ?? null });

    const handleOnOpenAttachment = (attachment: EmailAttachment) => {
        if (isOpening) {
            return; // do not let someone spam click and try open multiple times
        }
        openDocument(attachment.id, attachment.name).then();
    }

    const handleOnReply = () => onReply({ masterAccountId, projectId: data?.projectId ?? null });

    const handleOnReplyAll = () => onReplyAll({ masterAccountId, projectId: data?.projectId ?? null });

    const handleOnUnFile = (email: EmailDisplayContents) => {
        openConfirmModal({
            dialogTitle: "Un-file Email?",
            message: "Are you sure you want to un-file this email?",
            confirmButtonText: "Yes, Un-file",
            confirmButtonVariant: "danger",
            cancelButtonText: "Cancel"
        }).then(
            (proceed) => {
                if (proceed)
                    onUnFile(email).then(() => {
                        setMailId(null);
                    });
            }
        );
    };

    return (
        <EmailViewPort
            allowFiling
            commands={{
                onFile: handleOnReFile,
                onForward: handleOnForward,
                onOpenAttachment: handleOnOpenAttachment,
                onReply: handleOnReply,
                onReplyAll: handleOnReplyAll,
                onUnFile: handleOnUnFile
            }}
            email={{
                attachments: data?.attachments?.map((e) => ({
                    id: e.documentId,
                    name: e.fileName,
                    size: e.fileSize,
                    type: e.documentType,
                } as EmailAttachment)) ?? [],
                body: data?.body ?? "",
                conversationId: data?.conversationId ?? "",
                fromEmail: data?.senderEmailAddress ?? "",
                id: data?.graphId ?? "",
                isFiled: true,
                isAssignedToProject: data?.projectId !== null && data?.projectId !== undefined,
                masterAccountId: data?.masterAccountId ?? null,
                projectId: data?.projectId ?? null,
                sentDate: data?.sentDate ?? "",
                subject: data?.subject ?? "",
                toRecipients: data?.recipientEmails ?? []
            }}
            loadOptions={{
                error,
                isLoading: isFetching
            }}
            showProjectSelect={showProjectSelect}
        />
    )
};

export default ClientEmail;